// src/utils/pageMeta.js
// ============================================================================
// SENDER-SIDE link preview metadata (founder report 2026-07-23: sharing a
// stream link from the browser showed "StreamWatch" + a blank card).
//
// Two preview pipelines exist:
//  - RECIPIENT-side crawlers (Facebook, X, Discord, a pasted URL in Messages)
//    fetch the URL fresh → serveSocialPreview injects per-user OG tags into
//    the raw HTML. Already handled server-side.
//  - SENDER-side share sheets (iOS/Android share button while ON the page)
//    read the LIVE DOM of the page being shared. After client-side navigation
//    the DOM still holds the default OG block from whatever URL booted the
//    SPA, and react-navigation stomps document.title with the route name
//    ("StreamWatch"). This helper keeps the DOM metadata in sync so the card
//    a user shares is the card their friends see.
//
// setPageMeta() swaps title/description/url/image on the live meta tags
// (snapshotting the defaults first); resetPageMeta() restores the snapshot —
// call it on unmount so a later share from a generic screen gets the default
// CritCash card, not a stale streamer card. Web no-ops everywhere else.
// ============================================================================

import { Platform } from 'react-native';

const TAG_SELECTORS = {
  description: 'meta[name="description"]',
  ogTitle: 'meta[property="og:title"]',
  ogDescription: 'meta[property="og:description"]',
  ogUrl: 'meta[property="og:url"]',
  ogImage: 'meta[property="og:image"]',
  twTitle: 'meta[name="twitter:title"]',
  twDescription: 'meta[name="twitter:description"]',
  twImage: 'meta[name="twitter:image"]',
};

// Canonical defaults, HARDCODED to mirror scripts/inject-og.js — they cannot
// be snapshotted from the DOM: a direct load of /u/... or /s/... serves the
// PERSONALIZED block (serveSocialPreview gives browsers the same HTML as
// crawlers), so "what was there at boot" may already be someone's card.
const SITE = 'https://www.critcash.com';
const DEFAULT_TITLE = 'CritCast | The streaming platform with the tools built in';
const DEFAULT_DESCRIPTION =
  'Go live on any game and run your own events and leagues, with your ' +
  'streams saved as videos. Free to start, and free to watch with no account.';
const DEFAULTS = {
  description: DEFAULT_DESCRIPTION,
  ogTitle: DEFAULT_TITLE,
  ogDescription: DEFAULT_DESCRIPTION,
  ogUrl: `${SITE}/`,
  ogImage: `${SITE}/media/og-card.jpg`,
  twTitle: DEFAULT_TITLE,
  twDescription: DEFAULT_DESCRIPTION,
  twImage: `${SITE}/media/og-card.jpg`,
};

const canRun = () => Platform.OS === 'web' && typeof document !== 'undefined';

const writeTag = (sel, content) => {
  if (content == null) return;
  const el = document.querySelector(sel);
  if (el) el.setAttribute('content', content);
};

/**
 * Update the live page metadata. All fields optional — omitted fields keep
 * their current value. `title` also sets document.title (after
 * react-navigation's formatter has run for the route).
 */
export function setPageMeta({ title, description, url, image } = {}) {
  if (!canRun()) return;
  if (title) {
    document.title = title;
    writeTag(TAG_SELECTORS.ogTitle, title);
    writeTag(TAG_SELECTORS.twTitle, title);
  }
  if (description) {
    writeTag(TAG_SELECTORS.description, description);
    writeTag(TAG_SELECTORS.ogDescription, description);
    writeTag(TAG_SELECTORS.twDescription, description);
  }
  if (url) writeTag(TAG_SELECTORS.ogUrl, url);
  if (image) {
    writeTag(TAG_SELECTORS.ogImage, image);
    writeTag(TAG_SELECTORS.twImage, image);
  }
}

/** Restore the default (build-time) metadata. Safe to call repeatedly. */
export function resetPageMeta() {
  if (!canRun()) return;
  // document.title is NOT restored here — react-navigation's documentTitle
  // formatter owns it and re-stamps on every route change anyway.
  for (const key of Object.keys(TAG_SELECTORS)) {
    writeTag(TAG_SELECTORS[key], DEFAULTS[key]);
  }
}
